import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const fieldDataTypes = [
  "Integer",
  "Decimal",
  "SingleLineText",
  "MultiLineText",
  "Select",
  "MultiSelect",
  "Attachment",
  "Date",
  "DateTime",
  "Boolean",
];

const emptyDraft = {
  fieldDefinitionId: "",
  fieldName: "",
  fieldGroupingName: "",
  sortOrder: 0,
  fieldDataType: "SingleLineText",
  defaultValue: "",
  optionsText: "",
};

const TradingMarketNegotiationMetadataInput = ({
  allowEdit,
  field,
  isNew,
  marketId,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation(["trading"]);
  const [draft, setDraft] = useState(emptyDraft);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isNew || !field) {
      setDraft(emptyDraft);
      return;
    }

    setDraft({
      fieldDefinitionId: field.fieldDefinitionId,
      fieldName: field.fieldName || "",
      fieldGroupingName: field.fieldGroupingName || "",
      sortOrder: field.sortOrder || 0,
      fieldDataType: field.fieldDataType || "SingleLineText",
      defaultValue: field.defaultValue || "",
      optionsText: formatOptions(field.options || []),
    });
  }, [field, isNew]);

  const handleChange = ({ currentTarget: input }) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      [input.name]: input.value,
    }));
  };

  const formatOptions = (options) => {
    return [...options]
      .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0))
      .map((option) =>
        option.label && option.label !== option.value
          ? `${option.value}|${option.label}`
          : option.value
      )
      .join("\n");
  };

  const parseOptions = () => {
    if (draft.fieldDataType !== "Select" && draft.fieldDataType !== "MultiSelect") {
      return [];
    }

    const existingOptionsByValue = new Map(
      (field?.options || []).map((option) => [
        (option.value || "").toLowerCase(),
        option,
      ])
    );

    return draft.optionsText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line, index) => {
        const separatorIndex = line.indexOf("|");
        const value = separatorIndex >= 0 ? line.slice(0, separatorIndex).trim() : line;
        const label = separatorIndex >= 0 ? line.slice(separatorIndex + 1).trim() : value;
        const existingOption = existingOptionsByValue.get(value.toLowerCase());

        return {
          fieldValueOptionId: existingOption?.fieldValueOptionId || uuid(),
          value,
          label,
          sortOrder: index + 1,
          active: true,
          eventId: uuid(),
        };
      });
  };

  const validateDraft = () => {
    if (!draft.fieldName.trim()) {
      toast.error(t("FieldNameRequired"));
      return false;
    }

    if (!draft.fieldGroupingName.trim()) {
      toast.error(t("FieldGroupingNameRequired"));
      return false;
    }

    if (
      (draft.fieldDataType === "Select" || draft.fieldDataType === "MultiSelect") &&
      parseOptions().length === 0
    ) {
      toast.error(t("TradeNegotiationMetadataOptionsRequired"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateDraft()) return;

    const payload = {
      fieldDefinitionId: draft.fieldDefinitionId || uuid(),
      marketId,
      fieldName: draft.fieldName,
      fieldGroupingName: draft.fieldGroupingName,
      sortOrder: Number(draft.sortOrder || 0),
      fieldDataType: draft.fieldDataType,
      defaultValue: draft.defaultValue || null,
      active: true,
      eventId: uuid(),
      options: parseOptions(),
    };

    setIsSaving(true);
    try {
      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <fieldset disabled={!allowEdit || isSaving}>
        <div className="row">
          <Form.Group className="col-md-6 mb-3" controlId="trade-negotiation-field-name">
            <Form.Label>{t("FieldName")}</Form.Label>
            <Form.Control
              size="sm"
              name="fieldName"
              value={draft.fieldName}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="col-md-6 mb-3" controlId="trade-negotiation-field-grouping-name">
            <Form.Label>{t("FieldGroupingName")}</Form.Label>
            <Form.Control
              size="sm"
              name="fieldGroupingName"
              value={draft.fieldGroupingName}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="col-md-6 mb-3" controlId="trade-negotiation-sort-order">
            <Form.Label>{t("SortOrder")}</Form.Label>
            <Form.Control
              size="sm"
              name="sortOrder"
              type="number"
              value={draft.sortOrder}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="col-md-6 mb-3" controlId="trade-negotiation-field-data-type">
            <Form.Label>{t("FieldDataType")}</Form.Label>
            <Form.Control
              as="select"
              size="sm"
              name="fieldDataType"
              value={draft.fieldDataType}
              onChange={handleChange}
            >
              {fieldDataTypes.map((dataType) => (
                <option key={dataType} value={dataType}>
                  {t(dataType)}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group className="col-md-6 mb-3" controlId="trade-negotiation-default-value">
            <Form.Label>{t("DefaultValue")}</Form.Label>
            <Form.Control
              size="sm"
              name="defaultValue"
              value={draft.defaultValue}
              onChange={handleChange}
            />
          </Form.Group>
          {(draft.fieldDataType === "Select" || draft.fieldDataType === "MultiSelect") && (
            <Form.Group className="col-12 mb-3" controlId="trade-negotiation-options">
              <Form.Label>{t("Values")}</Form.Label>
              <Form.Control
                as="textarea"
                name="optionsText"
                rows={5}
                value={draft.optionsText}
                onChange={handleChange}
                placeholder={t("TradeNegotiationMetadataValuesPlaceholder")}
              />
              <Form.Text className="text-muted">
                {t("TradeNegotiationMetadataValuesHelpText")}
              </Form.Text>
            </Form.Group>
          )}
        </div>
        <div className="d-flex justify-content-end">
          <Button variant="secondary" size="sm" className="mr-2" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" size="sm" type="submit">
            {isNew ? t("CreateField") : t("SaveFieldChanges")}
          </Button>
        </div>
      </fieldset>
    </Form>
  );
};

export default TradingMarketNegotiationMetadataInput;